import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Coffee, LogOut, Loader2, ChevronDown } from 'lucide-react';
import { useWorkforce } from './WorkforceContext';
import { cn } from '../../lib/utils';

const parse = (iso) => (iso ? new Date(iso).getTime() : 0);
const fmt = (secs) => {
  secs = Math.max(0, Math.floor(secs));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

// Worked time across all of today's shifts (handles multiple clock-in/out cycles).
const workedSecs = (s) => {
  const now = Date.now();
  const shifts = s.shifts || [];
  let total = 0;
  for (const sh of shifts) {
    const inT = parse(sh.in);
    const outT = sh.out ? parse(sh.out) : now;
    if (inT) total += Math.max(0, (outT - inT) / 1000);
  }
  return total - (s.break_sec || 0);
};

const WorkforceTopControl = () => {
  const navigate = useNavigate();
  const { loading, enabled, session, busy, checkIn, breakStart, breakEnd, finish } = useWorkforce();
  const [menu, setMenu] = useState(false);
  const [, force] = useState(0);
  const ref = useRef(null);

  useEffect(() => { const id = setInterval(() => force((n) => n + 1), 1000); return () => clearInterval(id); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  if (loading || !enabled) return null;

  const status = session?.status || 'off';
  const now = Date.now();
  const onClock = status === 'working' || status === 'on_break';

  let timer = '';
  if (status === 'working') timer = fmt(workedSecs(session));
  else if (status === 'on_break') timer = fmt((now - parse(session.on_break_since)) / 1000);

  const run = (fn) => async () => { setMenu(false); try { await fn(); } catch { /* toast handled elsewhere */ } };

  // Off the clock (never checked in, or clocked out — ready for another shift).
  if (!onClock) {
    return (
      <button
        type="button"
        onClick={run(checkIn)}
        disabled={busy}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-500 text-white text-[13px] font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-60"
        data-testid="topbar-clock-in"
        title={status === 'finished' ? 'Start another shift' : 'Clock in for the day'}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
        Clock In
      </button>
    );
  }

  const dot = status === 'working' ? 'bg-emerald-500' : 'bg-amber-500';
  const label = status === 'working' ? 'Working' : 'On Break';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setMenu((v) => !v)}
        className="inline-flex items-center gap-2 h-8 pl-2.5 pr-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
        data-testid="topbar-clock-control"
      >
        <span className={cn('relative w-2 h-2 rounded-full', dot)}>
          {status === 'working' && <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />}
        </span>
        <span className="text-[13px] font-semibold text-slate-700 tabular-nums" data-testid="topbar-clock-timer">{timer}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>

      {menu && (
        <div className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl border border-slate-200 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.25)] overflow-hidden z-50" data-testid="topbar-clock-menu">
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className={cn('w-2 h-2 rounded-full', dot)} />
              <span className="text-[13px] font-semibold text-slate-800">{label}</span>
              <span className="ml-auto text-[13px] font-bold text-slate-900 tabular-nums">{timer}</span>
            </div>
            {session.shift_count > 1 && <p className="text-[11px] text-slate-400 mt-1">Shift {session.shift_count} today</p>}
          </div>

          {status === 'working' ? (
            <button type="button" onClick={run(breakStart)} disabled={busy} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-medium text-slate-700 hover:bg-amber-50 transition-colors" data-testid="topbar-break-start">
              <Coffee className="w-4 h-4 text-amber-500" /> Start break
            </button>
          ) : (
            <button type="button" onClick={run(breakEnd)} disabled={busy} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-medium text-slate-700 hover:bg-emerald-50 transition-colors" data-testid="topbar-break-end">
              <Play className="w-4 h-4 text-emerald-500" /> Resume work
            </button>
          )}
          <button type="button" onClick={run(finish)} disabled={busy} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100" data-testid="topbar-clock-out">
            <LogOut className="w-4 h-4 text-slate-500" /> Clock Out
          </button>
          <button type="button" onClick={() => { setMenu(false); navigate('/my-activity'); }} className="w-full px-4 py-2.5 text-left text-[12.5px] font-semibold text-[#3A6AFF] hover:bg-blue-50 transition-colors border-t border-slate-100" data-testid="topbar-my-activity">
            View My Activity
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkforceTopControl;
